import { useEffect, useState } from 'react';
import { MapPin, Plus, Edit, Trash2, Check, X, Home, Building2 } from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export const AccountAddresses = () => {
  const [addresses, setAddresses] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editingAddress, setEditingAddress] = useState(null);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(null);
  const [saving, setSaving] = useState(false);
  
  const [formData, setFormData] = useState({
    label: 'Home',
    full_name: '',
    phone: '',
    address_line_1: '',
    address_line_2: '',
    city: '',
    state: '',
    pincode: '',
    is_default: false
  });

  useEffect(() => {
    loadAddresses();
  }, []);

  const loadAddresses = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/account/addresses`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setAddresses(response.data.addresses || []);
    } catch (error) {
      console.error('Error loading addresses:', error);
    } finally {
      setLoading(false);
    }
  };

  const openAddModal = () => {
    setEditingAddress(null);
    setFormData({
      label: 'Home',
      full_name: '',
      phone: '',
      address_line_1: '',
      address_line_2: '',
      city: '',
      state: '',
      pincode: '',
      is_default: addresses.length === 0
    });
    setShowModal(true);
  };

  const openEditModal = (address) => {
    setEditingAddress(address);
    setFormData({
      label: address.label || 'Home',
      full_name: address.full_name || '',
      phone: address.phone || '',
      address_line_1: address.address_line_1 || '',
      address_line_2: address.address_line_2 || '',
      city: address.city || '',
      state: address.state || '',
      pincode: address.pincode || '',
      is_default: address.is_default || false
    });
    setShowModal(true);
  };

  const handleSave = async () => {
    if (!formData.full_name || !formData.phone || !formData.address_line_1 || !formData.city || !formData.state || !formData.pincode) {
      toast.error('Please fill all required fields');
      return;
    }

    setSaving(true);
    try {
      const token = localStorage.getItem('token');
      
      if (editingAddress) {
        await axios.put(`${API}/account/addresses/${editingAddress.id}`, formData, {
          headers: { Authorization: `Bearer ${token}` }
        });
        toast.success('Address updated');
      } else {
        await axios.post(`${API}/account/addresses`, formData, {
          headers: { Authorization: `Bearer ${token}` }
        });
        toast.success('Address added');
      }
      
      setShowModal(false);
      loadAddresses();
    } catch (error) {
      toast.error('Failed to save address');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (addressId) => {
    try {
      const token = localStorage.getItem('token');
      await axios.delete(`${API}/account/addresses/${addressId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Address deleted');
      setShowDeleteConfirm(null);
      loadAddresses();
    } catch (error) {
      toast.error('Failed to delete address');
    }
  };

  const setAsDefault = async (addressId) => {
    try {
      const token = localStorage.getItem('token');
      await axios.put(`${API}/account/addresses/${addressId}`, { is_default: true }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Default address updated');
      loadAddresses();
    } catch (error) {
      toast.error('Failed to update');
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <div className="w-8 h-8 border-2 border-gold border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="account-addresses">
      <div className="flex items-center justify-between">
        <h1 className="font-heading text-2xl font-light text-charcoal">Saved Addresses</h1>
        <button
          onClick={openAddModal}
          className="flex items-center gap-2 bg-gold text-white px-4 py-2 text-sm font-medium hover:bg-gold-light transition-colors"
        >
          <Plus className="w-4 h-4" />
          Add Address
        </button>
      </div>

      {addresses.length > 0 ? (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {addresses.map((address) => (
            <div
              key={address.id}
              className={`bg-white border p-6 relative ${
                address.is_default ? 'border-gold' : 'border-charcoal/10'
              }`}
            >
              {address.is_default && (
                <span className="absolute top-2 right-2 bg-gold text-white text-xs px-2 py-1">
                  DEFAULT
                </span>
              )}
              
              <div className="flex items-center gap-2 mb-3">
                {address.label === 'Home' ? (
                  <Home className="w-5 h-5 text-gold" />
                ) : (
                  <Building2 className="w-5 h-5 text-gold" />
                )}
                <span className="font-medium text-charcoal">{address.label}</span>
              </div>
              
              <p className="font-medium text-charcoal">{address.full_name}</p>
              <p className="text-sm text-charcoal-light mt-1">{address.address_line_1}</p>
              {address.address_line_2 && (
                <p className="text-sm text-charcoal-light">{address.address_line_2}</p>
              )}
              <p className="text-sm text-charcoal-light">
                {address.city}, {address.state} - {address.pincode}
              </p>
              <p className="text-sm text-charcoal-light mt-2">Phone: {address.phone}</p>
              
              <div className="flex items-center gap-2 mt-4 pt-4 border-t border-charcoal/10">
                <button
                  onClick={() => openEditModal(address)}
                  className="flex items-center gap-1 text-sm text-charcoal-light hover:text-gold transition-colors"
                >
                  <Edit className="w-4 h-4" />
                  Edit
                </button>
                <button
                  onClick={() => setShowDeleteConfirm(address.id)}
                  className="flex items-center gap-1 text-sm text-charcoal-light hover:text-red-600 transition-colors"
                >
                  <Trash2 className="w-4 h-4" />
                  Delete
                </button>
                {!address.is_default && (
                  <button
                    onClick={() => setAsDefault(address.id)}
                    className="flex items-center gap-1 text-sm text-gold hover:underline ml-auto"
                  >
                    <Check className="w-4 h-4" />
                    Set as Default
                  </button>
                )}
              </div>
            </div>
          ))}
        </div>
      ) : (
        <div className="bg-white border border-charcoal/10 p-12 text-center">
          <MapPin className="w-16 h-16 mx-auto text-charcoal-light mb-4" />
          <h3 className="font-heading text-xl font-light text-charcoal mb-2">No saved addresses</h3>
          <p className="text-charcoal-light mb-4">Add an address for faster checkout</p>
          <button
            onClick={openAddModal}
            className="inline-block bg-gold text-white px-6 py-2 text-sm font-medium hover:bg-gold-light transition-colors"
          >
            Add Address
          </button>
        </div>
      )}

      {/* Add/Edit Modal */}
      {showModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-charcoal/50">
          <div className="bg-white w-full max-w-md max-h-[90vh] overflow-y-auto">
            <div className="sticky top-0 bg-white border-b border-charcoal/10 p-6 flex items-center justify-between">
              <h2 className="font-heading text-xl font-light text-charcoal">
                {editingAddress ? 'Edit Address' : 'Add Address'}
              </h2>
              <button onClick={() => setShowModal(false)} className="text-charcoal-light hover:text-charcoal">
                <X className="w-5 h-5" />
              </button>
            </div>
            
            <div className="p-6 space-y-4">
              {/* Label */}
              <div>
                <label className="block text-xs tracking-wider uppercase text-charcoal-light font-medium mb-2">
                  Address Type
                </label>
                <div className="flex gap-2">
                  {['Home', 'Office', 'Other'].map(type => (
                    <button
                      key={type}
                      onClick={() => setFormData({ ...formData, label: type })}
                      className={`px-4 py-2 text-sm border transition-colors ${
                        formData.label === type
                          ? 'border-gold bg-gold/10 text-gold'
                          : 'border-charcoal/10 text-charcoal-light hover:border-gold/50'
                      }`}
                    >
                      {type}
                    </button>
                  ))}
                </div>
              </div>

              {/* Full Name */}
              <div>
                <label className="block text-xs tracking-wider uppercase text-charcoal-light font-medium mb-2">
                  Full Name *
                </label>
                <input
                  type="text"
                  value={formData.full_name}
                  onChange={(e) => setFormData({ ...formData, full_name: e.target.value })}
                  className="w-full h-11 px-4 border border-charcoal/10 focus:border-gold outline-none"
                />
              </div>

              {/* Phone */}
              <div>
                <label className="block text-xs tracking-wider uppercase text-charcoal-light font-medium mb-2">
                  Phone Number *
                </label>
                <input
                  type="tel"
                  value={formData.phone}
                  onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                  className="w-full h-11 px-4 border border-charcoal/10 focus:border-gold outline-none"
                />
              </div>

              {/* Address Line 1 */}
              <div>
                <label className="block text-xs tracking-wider uppercase text-charcoal-light font-medium mb-2">
                  Address Line 1 *
                </label>
                <input
                  type="text"
                  value={formData.address_line_1}
                  onChange={(e) => setFormData({ ...formData, address_line_1: e.target.value })}
                  className="w-full h-11 px-4 border border-charcoal/10 focus:border-gold outline-none"
                  placeholder="House no, Building, Street"
                />
              </div>

              {/* Address Line 2 */}
              <div>
                <label className="block text-xs tracking-wider uppercase text-charcoal-light font-medium mb-2">
                  Address Line 2
                </label>
                <input
                  type="text"
                  value={formData.address_line_2}
                  onChange={(e) => setFormData({ ...formData, address_line_2: e.target.value })}
                  className="w-full h-11 px-4 border border-charcoal/10 focus:border-gold outline-none"
                  placeholder="Landmark, Area"
                />
              </div>

              {/* City & State */}
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-xs tracking-wider uppercase text-charcoal-light font-medium mb-2">
                    City *
                  </label>
                  <input
                    type="text"
                    value={formData.city}
                    onChange={(e) => setFormData({ ...formData, city: e.target.value })}
                    className="w-full h-11 px-4 border border-charcoal/10 focus:border-gold outline-none"
                  />
                </div>
                <div>
                  <label className="block text-xs tracking-wider uppercase text-charcoal-light font-medium mb-2">
                    State *
                  </label>
                  <input
                    type="text"
                    value={formData.state}
                    onChange={(e) => setFormData({ ...formData, state: e.target.value })}
                    className="w-full h-11 px-4 border border-charcoal/10 focus:border-gold outline-none"
                  />
                </div>
              </div>

              {/* Pincode */}
              <div>
                <label className="block text-xs tracking-wider uppercase text-charcoal-light font-medium mb-2">
                  Pincode *
                </label>
                <input
                  type="text"
                  value={formData.pincode}
                  onChange={(e) => setFormData({ ...formData, pincode: e.target.value })}
                  className="w-full h-11 px-4 border border-charcoal/10 focus:border-gold outline-none"
                  maxLength={6}
                />
              </div>

              {/* Default */}
              <label className="flex items-center gap-3 cursor-pointer">
                <input
                  type="checkbox"
                  checked={formData.is_default}
                  onChange={(e) => setFormData({ ...formData, is_default: e.target.checked })}
                  className="w-5 h-5 accent-gold"
                />
                <span className="text-sm text-charcoal">Set as default address</span>
              </label>
            </div>
            
            <div className="sticky bottom-0 bg-white border-t border-charcoal/10 p-6 flex justify-end gap-3">
              <button
                onClick={() => setShowModal(false)}
                className="px-6 py-2 border border-charcoal/20 text-charcoal text-sm"
              >
                Cancel
              </button>
              <button
                onClick={handleSave}
                disabled={saving}
                className="px-6 py-2 bg-gold text-white text-sm disabled:opacity-50"
              >
                {saving ? 'Saving...' : editingAddress ? 'Update Address' : 'Add Address'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Delete Confirmation */}
      {showDeleteConfirm && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-charcoal/50">
          <div className="bg-white p-6 max-w-sm w-full">
            <h3 className="font-heading text-lg font-light text-charcoal mb-2">Delete Address?</h3>
            <p className="text-charcoal-light text-sm mb-6">This action cannot be undone.</p>
            <div className="flex justify-end gap-3">
              <button
                onClick={() => setShowDeleteConfirm(null)}
                className="px-4 py-2 border border-charcoal/20 text-charcoal text-sm"
              >
                Cancel
              </button>
              <button
                onClick={() => handleDelete(showDeleteConfirm)}
                className="px-4 py-2 bg-red-600 text-white text-sm"
              >
                Delete
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AccountAddresses;
